// src/components/services/ServiceList.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  fetchServices,
  deleteService,
  toggleServiceStatus,
  clearServiceError,
  clearServiceSuccess,
  setServiceFilters,
} from '../../store/actions/serviceActions';
import Pagination from '../common/Pagination';
import SearchBar from '../common/SearchBar';
import LoadingSpinner from '../common/LoadingSpinner';
import Alert from '../common/Alert';
import ConfirmDialog from '../common/ConfirmDialog';
import FilterBar from '../common/FilterBar';

const ServiceList = () => {
  const dispatch = useDispatch();
  const { services, loading, error, success, meta, filters } = useSelector(
    (state) => state.services
  );

  const [selectedService, setSelectedService] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  
  const initialMount = useRef(true);
  const filtersRef = useRef(filters);

  const styles = {
    container: {
      maxWidth: '1280px',
      margin: '0 auto',
      padding: '2rem 1rem',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1.5rem',
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: 700,
      color: '#111827',
      margin: 0,
    },
    headerActions: {
      display: 'flex',
      gap: '1rem',
      alignItems: 'center',
    },
    createButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#2563eb',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      textDecoration: 'none',
      display: 'inline-flex',
      alignItems: 'center',
      transition: 'background-color 0.2s',
    },
    createButtonHover: {
      backgroundColor: '#1d4ed8',
    },
    bulkDeleteButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#dc2626',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      transition: 'background-color 0.2s',
    },
    bulkDeleteButtonHover: {
      backgroundColor: '#b91c1c',
    },
    bulkDeleteButtonDisabled: {
      backgroundColor: '#e5e7eb',
      color: '#9ca3af',
      cursor: 'not-allowed',
    },
    filterBar: {
      marginBottom: '1rem',
    },
    tableContainer: {
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      overflow: 'hidden',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
    },
    tableHeader: {
      backgroundColor: '#f9fafb',
      borderBottom: '1px solid #e5e7eb',
    },
    tableHeaderCell: {
      padding: '0.75rem 1.5rem',
      textAlign: 'left',
      fontSize: '0.75rem',
      fontWeight: 500,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      color: '#6b7280',
      borderBottom: '1px solid #e5e7eb',
    },
    tableHeaderCellCheckbox: {
      padding: '0.75rem 1.5rem',
      width: '40px',
    },
    tableHeaderCellRight: {
      padding: '0.75rem 1.5rem',
      textAlign: 'right',
      fontSize: '0.75rem',
      fontWeight: 500,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      color: '#6b7280',
      borderBottom: '1px solid #e5e7eb',
    },
    tableRow: {
      borderBottom: '1px solid #e5e7eb',
      transition: 'background-color 0.2s',
    },
    tableRowHover: {
      backgroundColor: '#f9fafb',
    },
    tableCell: {
      padding: '1rem 1.5rem',
      fontSize: '0.875rem',
      color: '#111827',
    },
    tableCellCheckbox: {
      padding: '1rem 1.5rem',
      width: '40px',
    },
    tableCellRight: {
      padding: '1rem 1.5rem',
      fontSize: '0.875rem',
      color: '#111827',
      textAlign: 'right',
    },
    serviceName: {
      fontWeight: 500,
      color: '#111827',
    },
    badge: {
      display: 'inline-flex',
      padding: '0.25rem 0.5rem',
      fontSize: '0.75rem',
      fontWeight: 500,
      borderRadius: '9999px',
    },
    activeBadge: {
      backgroundColor: '#d1fae5',
      color: '#065f46',
    },
    inactiveBadge: {
      backgroundColor: '#fee2e2',
      color: '#991b1b',
    },
    agencyCount: {
      fontSize: '0.875rem',
      color: '#6b7280',
    },
    actionsContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: '0.5rem',
    },
    actionButton: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0.25rem',
      border: 'none',
      background: 'none',
      cursor: 'pointer',
      color: '#6b7280',
      transition: 'color 0.2s',
      borderRadius: '0.375rem',
    },
    viewButton: {
      color: '#2563eb',
    },
    viewButtonHover: {
      color: '#1d4ed8',
    },
    editButton: {
      color: '#4f46e5',
    },
    editButtonHover: {
      color: '#4338ca',
    },
    toggleButton: {
      color: '#059669',
    },
    toggleButtonHover: {
      color: '#047857',
    },
    deleteButton: {
      color: '#dc2626',
    },
    deleteButtonHover: {
      color: '#b91c1c',
    },
    icon: {
      width: '1.25rem',
      height: '1.25rem',
    },
    emptyState: {
      textAlign: 'center',
      padding: '3rem 1.5rem',
      color: '#6b7280',
      fontSize: '0.875rem',
    },
    checkbox: {
      width: '1rem',
      height: '1rem',
      cursor: 'pointer',
    },
  };

  const fetchServicesData = useCallback(() => {
    dispatch(fetchServices(filters));
  }, [dispatch, filters]);

  useEffect(() => {
    if (initialMount.current) {
      initialMount.current = false;
      fetchServicesData();
    } else {
      if (JSON.stringify(filtersRef.current) !== JSON.stringify(filters)) {
        filtersRef.current = filters;
        fetchServicesData();
      }
    }
  }, [fetchServicesData, filters]);

  useEffect(() => {
    return () => {
      dispatch(clearServiceError());
      dispatch(clearServiceSuccess());
    };
  }, [dispatch]);

  const handleSearch = useCallback((searchTerm) => {
    dispatch(setServiceFilters({
      ...filters,
      search: searchTerm,
      page: 1,
    }));
  }, [dispatch, filters]);

  const handleFilterChange = useCallback((newFilters) => {
    dispatch(setServiceFilters({
      ...filters,
      ...newFilters,
      page: 1,
    }));
  }, [dispatch, filters]);

  const handlePageChange = useCallback((page) => {
    dispatch(setServiceFilters({
      ...filters,
      page,
    }));
  }, [dispatch, filters]);

  const handleDeleteClick = useCallback((service) => {
    setSelectedService(service);
    setShowDeleteConfirm(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    try {
      await dispatch(deleteService(selectedService.id));
      setShowDeleteConfirm(false);
      setSelectedService(null);
    } catch (error) {
      console.error('Delete failed:', error);
    }
  }, [dispatch, selectedService]);

  const handleCloseDelete = useCallback(() => {
    setShowDeleteConfirm(false);
    setSelectedService(null);
  }, []);

  const handleToggleStatus = useCallback(async (service) => {
    try {
      await dispatch(toggleServiceStatus(service.id));
    } catch (error) {
      console.error('Toggle status failed:', error);
    }
  }, [dispatch]);

  const handleSelectAll = useCallback((e) => {
    if (e.target.checked) {
      setSelectedIds(services.map(service => service.id));
    } else {
      setSelectedIds([]);
    }
  }, [services]);

  const handleSelectOne = useCallback((id) => {
    setSelectedIds(prev => {
      if (prev.includes(id)) {
        return prev.filter(item => item !== id);
      } else {
        return [...prev, id];
      }
    });
  }, []);

  const handleBulkDeleteClick = useCallback(() => {
    if (selectedIds.length > 0) {
      setShowBulkDeleteConfirm(true);
    }
  }, [selectedIds]);

  const handleBulkDeleteConfirm = useCallback(async () => {
    try {
      // await dispatch(bulkDeleteServices(selectedIds));
      setShowBulkDeleteConfirm(false);
      setSelectedIds([]);
    } catch (error) {
      console.error('Bulk delete failed:', error);
    }
  }, []);

  if (loading && !services.length) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Gestion des services</h1>
        <div style={styles.headerActions}>
          {selectedIds.length > 0 && (
            <button
              onClick={handleBulkDeleteClick}
              style={{
                ...styles.bulkDeleteButton,
                ...(selectedIds.length === 0 ? styles.bulkDeleteButtonDisabled : {}),
              }}
              onMouseEnter={(e) => {
                if (selectedIds.length > 0) {
                  e.currentTarget.style.backgroundColor = styles.bulkDeleteButtonHover.backgroundColor;
                }
              }}
              onMouseLeave={(e) => {
                if (selectedIds.length > 0) {
                  e.currentTarget.style.backgroundColor = styles.bulkDeleteButton.backgroundColor;
                }
              }}
            >
              <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Supprimer ({selectedIds.length})
            </button>
          )}
          <Link
            to="/admin/services/form"
            style={styles.createButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.createButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.createButton.backgroundColor;
            }}
          >
            Créer un nouveau service
          </Link>
        </div>
      </div>

      {error && (
        <Alert
          type="error"
          message={error}
          onClose={() => dispatch(clearServiceError())}
        />
      )}

      {success && (
        <Alert
          type="success"
          message={success}
          onClose={() => dispatch(clearServiceSuccess())}
        />
      )}

      <div style={styles.filterBar}>
        <FilterBar
          filters={[
            {
              type: 'select',
              name: 'isActive',
              label: 'Statut',
              options: [
                { value: '', label: 'Tous' },
                { value: 'true', label: 'Actif' },
                { value: 'false', label: 'Inactif' },
              ],
            },
          ]}
          onFilterChange={handleFilterChange}
          initialValues={filters}
        />
      </div>

      <div style={styles.searchContainer}>
        <SearchBar 
          onSearch={handleSearch} 
          placeholder="Rechercher des services par nom ou description..." 
          fullWidth
        />
      </div>

      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead style={styles.tableHeader}>
            <tr>
              <th style={styles.tableHeaderCellCheckbox}>
                <input
                  type="checkbox"
                  checked={selectedIds.length === services.length && services.length > 0}
                  onChange={handleSelectAll}
                  style={styles.checkbox}
                />
              </th>
              <th style={styles.tableHeaderCell}>Service</th>
              <th style={styles.tableHeaderCell}>Statut</th>
              <th style={styles.tableHeaderCell}>Agences</th>
              <th style={styles.tableHeaderCellRight}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {services.map((service) => (
              <tr 
                key={service.id} 
                style={styles.tableRow}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = styles.tableRowHover.backgroundColor;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <td style={styles.tableCellCheckbox}>
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(service.id)}
                    onChange={() => handleSelectOne(service.id)}
                    style={styles.checkbox}
                  />
                </td>
                <td style={styles.tableCell}>
                  <div>
                    <div style={styles.serviceName}>{service.name}</div>
                    <div style={styles.agencyCount}>{service.description}</div>
                  </div>
                </td>
                <td style={styles.tableCell}>
                  <span
                    style={{
                      ...styles.badge,
                      ...(service.isActive ? styles.activeBadge : styles.inactiveBadge),
                    }}
                  >
                    {service.isActive ? 'Actif' : 'Inactif'}
                  </span>
                </td>
                <td style={styles.tableCell}>
                  <span style={styles.agencyCount}>
                    {service._count?.agencies || 0} agences
                  </span>
                </td>
                <td style={styles.tableCellRight}>
                  <div style={styles.actionsContainer}>
                    <Link
                      to={`/admin/services/${service.id}/details`}
                      style={{
                        ...styles.actionButton,
                        ...styles.viewButton,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = styles.viewButtonHover.color;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = styles.viewButton.color;
                      }}
                      title="Voir les détails"
                    >
                      <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </Link>

                    <Link
                      to={`/admin/services/${service.id}/edit`}
                      style={{
                        ...styles.actionButton,
                        ...styles.editButton,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = styles.editButtonHover.color;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = styles.editButton.color;
                      }}
                      title="Modifier"
                    >
                      <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </Link>

                    <button
                      onClick={() => handleToggleStatus(service)}
                      style={{
                        ...styles.actionButton,
                        ...styles.toggleButton,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = styles.toggleButtonHover.color;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = styles.toggleButton.color;
                      }}
                      title={service.isActive ? 'Désactiver' : 'Activer'}
                    >
                      {service.isActive ? (
                        <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                        </svg>
                      ) : (
                        <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                        </svg>
                      )}
                    </button>

                    <button
                      onClick={() => handleDeleteClick(service)}
                      style={{
                        ...styles.actionButton,
                        ...styles.deleteButton,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = styles.deleteButtonHover.color;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = styles.deleteButton.color;
                      }}
                      title="Supprimer"
                    >
                      <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {services.length === 0 && !loading && (
          <div style={styles.emptyState}>
            Aucun service trouvé correspondant à vos critères
          </div>
        )}

        <Pagination
          currentPage={meta.page}
          totalPages={meta.totalPages}
          onPageChange={handlePageChange}
        />
      </div>

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={handleCloseDelete}
        onConfirm={handleDeleteConfirm}
        title="Supprimer le service"
        message={`Êtes-vous sûr de vouloir supprimer le service "${selectedService?.name}" ?`}
        confirmText="Supprimer"
        cancelText="Annuler"
        type="danger"
      />

      <ConfirmDialog
        isOpen={showBulkDeleteConfirm}
        onClose={() => setShowBulkDeleteConfirm(false)}
        onConfirm={handleBulkDeleteConfirm}
        title="Supprimer plusieurs services"
        message={`Êtes-vous sûr de vouloir supprimer ${selectedIds.length} service(s) ? Cette action est irréversible.`}
        confirmText="Supprimer"
        cancelText="Annuler"
        type="danger"
      />
    </div>
  );
};

export default ServiceList;